import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { onAuthStateChanged, signOut as fbSignOut } from 'firebase/auth';
import { getDoc, onSnapshot, updateDoc } from 'firebase/firestore';
import { auth } from '../../firebase';
import { useLanguage } from '../../contexts/LanguageContext';
import { createUserProfile, normalizeUser, userRef } from '../lib/services';
import { syncSubscription } from '../lib/cloud';

const SessionContext = createContext(null);

// Set by the signup form while it writes the user doc itself, so the "missing
// profile" fallback below doesn't race it with a default profile.
let signupInProgress = false;
export function setSignupInProgress(v) {
  signupInProgress = v;
}

const THEME_KEY = 'gm-theme';
const BG_KEY = 'gm-backgrounds';

function readPref(key, fallback) {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch (e) {
    return fallback;
  }
}

function writePref(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // ignore (private mode)
  }
}

export function SessionProvider({ children }) {
  const [authUser, setAuthUser] = useState(undefined); // undefined = resolving
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [theme, setThemeState] = useState(() => readPref(THEME_KEY, 'light'));
  const [backgrounds, setBackgroundsState] = useState(() => readPref(BG_KEY, 'on') !== 'off');
  const [isDark, setIsDark] = useState(false);
  const [isAdmin, setIsAdmin] = useState(null); // null = resolving

  // Admin role = Firebase Auth custom claim `admin: true` (set_admin.py).
  const refreshAdmin = useCallback(async (force = false) => {
    const current = auth.currentUser;
    if (!current) {
      setIsAdmin(false);
      return false;
    }
    try {
      const result = await current.getIdTokenResult(force);
      const admin = result.claims?.admin === true;
      setIsAdmin(admin);
      return admin;
    } catch (e) {
      setIsAdmin(false);
      return false;
    }
  }, []);

  useEffect(() => {
    if (authUser === undefined) return;
    setIsAdmin(authUser ? null : false);
    if (authUser) refreshAdmin(false);
  }, [authUser, refreshAdmin]);

  useEffect(() => onAuthStateChanged(auth, (u) => setAuthUser(u || null)), []);

  useEffect(() => {
    if (!authUser) {
      setUser(null);
      setUserLoading(authUser === undefined);
      return undefined;
    }
    setUserLoading(true);
    let creating = false;
    const unsub = onSnapshot(
      userRef(authUser.uid),
      async (snap) => {
        if (snap.exists()) {
          setUser(normalizeUser(snap.id, snap.data()));
          setUserLoading(false);
          return;
        }
        // First sign-in from a provider that skipped our signup form (e.g. Google):
        // create the same default profile the app creates.
        if (creating || signupInProgress) return;
        creating = true;
        try {
          const again = await getDoc(userRef(authUser.uid));
          if (!again.exists()) {
            const parts = (authUser.displayName || '').trim().split(/\s+/);
            await createUserProfile({
              uid: authUser.uid,
              email: authUser.email,
              firstName: parts[0] || 'User',
              lastName: parts.slice(1).join(' '),
              role: 'trainee',
            });
          }
        } catch (e) {
          setUserLoading(false);
        }
      },
      () => setUserLoading(false),
    );
    return unsub;
  }, [authUser]);

  // users/{uid}.language is shared with the mobile app: the account's language
  // wins on sign-in, and a change on either side follows on the other.
  const { language, setLanguage } = useLanguage();
  const remoteLanguage = user?.language;

  // Plans that come from RevenueCat (store or promo code) can expire while the
  // user only uses the web: re-check once per session, like the app does on open.
  const syncedUid = useRef(null);
  useEffect(() => {
    const source = user?.subscriptionSource;
    if (!user?.id || syncedUid.current === user.id) return;
    if (source !== 'store' && source !== 'rc_promo') return;
    syncedUid.current = user.id;
    syncSubscription().catch(() => {});
  }, [user?.id, user?.subscriptionSource]);
  const lastRemoteLanguage = useRef(null);
  const uid = user?.id;

  useEffect(() => {
    lastRemoteLanguage.current = null;
  }, [uid]);

  useEffect(() => {
    if (!uid || remoteLanguage === lastRemoteLanguage.current) return;
    lastRemoteLanguage.current = remoteLanguage;
    if (remoteLanguage === 'en' || remoteLanguage === 'tr') {
      if (remoteLanguage !== language) setLanguage(remoteLanguage);
    } else if (remoteLanguage !== 'es') {
      // 'es' is app-only for now: keep the site's language but don't overwrite the account's.
      updateDoc(userRef(uid), { language }).catch(() => {});
    }
    // Only reacts to the account's value; local changes are handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, remoteLanguage]);

  useEffect(() => {
    if (!uid || lastRemoteLanguage.current === null || language === lastRemoteLanguage.current) return;
    updateDoc(userRef(uid), { language }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  useEffect(() => {
    const root = document.documentElement;
    const apply = () => {
      const dark = theme === 'dark' || (theme === 'system' && window.matchMedia?.('(prefers-color-scheme: dark)').matches);
      root.setAttribute('data-gm-theme', dark ? 'dark' : 'light');
      setIsDark(dark);
      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta) meta.setAttribute('content', dark ? '#000000' : '#ffffff');
    };
    apply();
    if (theme !== 'system' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [theme]);

  const setTheme = useCallback((v) => {
    setThemeState(v);
    writePref(THEME_KEY, v);
  }, []);

  const setBackgrounds = useCallback((v) => {
    setBackgroundsState(v);
    writePref(BG_KEY, v ? 'on' : 'off');
  }, []);

  const signOut = useCallback(() => fbSignOut(auth), []);

  const value = useMemo(
    () => ({ authUser, user, loading: authUser === undefined || (authUser && userLoading), theme, isDark, setTheme, backgrounds, setBackgrounds, signOut, isAdmin, refreshAdmin }),
    [authUser, user, userLoading, theme, isDark, setTheme, backgrounds, setBackgrounds, signOut, isAdmin, refreshAdmin],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  return useContext(SessionContext);
}
